import React, { useMemo } from 'react'
import { ColDef } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { Card, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import agGridTheme from '../../../config/agGrid/theme'
import { useAgGridLocale } from '../../../shared/hooks/useAgGridLocale'
import { formatValueByType } from '../../../utils/helpers'
import { Transaction, Voucher } from '../../../pages/reporting/vouchers/types'
import { getTransactionColumnDefs } from '../../vouchers/components/VoucherTable/transactionColumnDefs'
import { ReconciliationCounterpartyGroup } from '../types'
import './reconciliationDetailGrid.css'

const { Text } = Typography

const noColumnBorderTheme = agGridTheme.withParams({ columnBorder: false })

const COLUMN_OVERRIDES: Record<string, ColDef<Transaction>> = {
  accountName: { width: 220 },
  description: { flex: 1, minWidth: 150 }
}

const amountFormatter = (params: { value?: number }) =>
  formatValueByType(params.value, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const hasLedgerAmounts = (voucher: Voucher) =>
  voucher.transactions.some(tx => tx.ledgerDebit != null || tx.ledgerCredit != null)

// Extra "Debet (SEK)" / "Kredit (SEK)" columns for the untouched subsidiary-ledger amounts.
// Spliced in right after `credit`; the ISO code comes from the voucher's ledger currency.
const withLedgerColumns = (baseColumnDefs: ColDef<Transaction>[], voucher: Voucher): ColDef<Transaction>[] => {
  const currency = voucher.ledgerCurrency ?? i18next.t('icReconciliation:ledgerOriginalShort', 'alkup.')
  const ledgerColumns: ColDef<Transaction>[] = [
    {
      headerName: `${i18next.t('vouchersPage:debit', 'Debet')} (${currency})`,
      field: 'ledgerDebit',
      width: 110,
      type: 'numericColumn',
      sortable: false,
      valueFormatter: amountFormatter
    },
    {
      headerName: `${i18next.t('vouchersPage:credit', 'Kredit')} (${currency})`,
      field: 'ledgerCredit',
      width: 110,
      type: 'numericColumn',
      sortable: false,
      valueFormatter: amountFormatter
    }
  ]
  const creditIndex = baseColumnDefs.findIndex(colDef => colDef.field === 'credit')
  const insertAt = creditIndex === -1 ? baseColumnDefs.length : creditIndex + 1
  return [...baseColumnDefs.slice(0, insertAt), ...ledgerColumns, ...baseColumnDefs.slice(insertAt)]
}

interface ReconciliationVoucherGroupsProps {
  groups: ReconciliationCounterpartyGroup[]
  reference?: string
}

const ReconciliationVoucherGroups: React.FC<ReconciliationVoucherGroupsProps> = ({ groups, reference }) => {
  const { t } = useTranslation()
  const localeText = useAgGridLocale()

  // Shared base columns (dimension columns depend on the full transaction set, so keep feeding
  // every group's lines); the per-voucher ledger columns are added per grid below.
  const baseColumnDefs = useMemo(
    () =>
      getTransactionColumnDefs(
        t,
        groups.flatMap(group => group.voucher.transactions)
      ).map(colDef =>
        colDef.field && COLUMN_OVERRIDES[colDef.field] ? { ...colDef, ...COLUMN_OVERRIDES[colDef.field] } : colDef
      ),
    [t, groups]
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBlock: 16 }}>
      {groups.map(group => {
        const { transactions } = group.voucher
        // Add the ledger columns only for a voucher that actually has original-currency amounts —
        // an all-reporting-currency voucher would just get two empty columns.
        const columnDefs = hasLedgerAmounts(group.voucher)
          ? withLedgerColumns(baseColumnDefs, group.voucher)
          : baseColumnDefs
        return (
          <Card
            key={`${group.companyName}-${group.voucher.guid}`}
            size="small"
            style={{ background: '#fafafa' }}
            title={<Text strong>{group.companyName}</Text>}
            extra={<Text type="secondary">{reference}</Text>}
            styles={{
              body: { padding: 8, margin: 0 },
              header: {
                borderBottom: 'none'
              }
            }}
          >
            <div className="reconciliation-detail-grid">
              <AgGridReact<Transaction>
                theme={noColumnBorderTheme}
                localeText={localeText}
                rowData={transactions}
                columnDefs={columnDefs}
                rowHeight={24}
                domLayout="autoHeight"
                getRowId={params => String(params.data.id)}
                defaultColDef={{
                  resizable: true,
                  sortable: false,
                  suppressHeaderMenuButton: true
                }}
              />
            </div>
          </Card>
        )
      })}
    </div>
  )
}

export default ReconciliationVoucherGroups
